import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { complianceService, subscriptionService } from '../../services/api';
import Navbar from '../Dashboard/components/Navbar';
import Footer from '../Dashboard/components/Footer';
import UpgradeModal from '../Shared/UpgradeModal';

import { getUserAttributes } from '../../services/cognito';
import PhoneVerificationModal from '../Shared/PhoneVerificationModal';

import PropertyForm, { PropertyFormData } from './PropertyForm';
import CertificateUploadStep from './CertificateUploadStepV2';
import BulkUploadStep from './BulkUploadStep';

const LIMITS: Record<string, number> = {
    'freemium': 1,
    'free_trial': 3,
    'extended_trial': 50,
    'landlord_pro_1': 6,
    'pro1': 6,
    'landlord_pro_2': 20,
    'pro2': 20,
    'pro3': 100,
    'enterprise': 9999
};

type WizardMode = 'SELECTION' | 'SINGLE_FORM' | 'SINGLE_CERT' | 'BULK';

export default function AddProperty() {
    const navigate = useNavigate();
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [showPhoneModal, setShowPhoneModal] = useState(false);
    const [checkingLimits, setCheckingLimits] = useState(true);

    const [mode, setMode] = useState<WizardMode>('SELECTION');
    const [createdPropertyId, setCreatedPropertyId] = useState<string | null>(null);
    const [upgradeReason, setUpgradeReason] = useState<string>('');

    useEffect(() => {
        const checkPrerequisites = async () => {
            try {
                const [propsRes, subsRes] = await Promise.all([
                    complianceService.getProperties(),
                    subscriptionService.getMySubscriptions()
                ]);

                // /api/properties returns { success, data: [...] }.
                const properties = Array.isArray((propsRes as any)?.data?.data)
                    ? (propsRes as any).data.data
                    : (Array.isArray((propsRes as any)?.data) ? (propsRes as any).data : []);
                const count = properties.length;

                // /api/subscription returns { success, data: plan } — a single
                // plan object, not a list. Normalise both shapes so .find never
                // runs on an envelope (BUG-ADD-PROP-01).
                const subsPayload: any = (subsRes as any)?.data?.data ?? (subsRes as any)?.data ?? {};
                const activeSub = Array.isArray(subsPayload)
                    ? (subsPayload.find((s: any) => s.status === 'active') || subsPayload[0])
                    : subsPayload;
                const planId = activeSub?.plan_tier || activeSub?.tier || 'freemium';

                const limit = LIMITS[planId.toLowerCase()] || 1;
                const isAdmin = localStorage.getItem('isAdmin') === 'true';

                if (count >= limit && !isAdmin) {
                    setShowUpgradeModal(true);
                }

            } catch (error) {
                console.error("Failed to check prerequisites", error);
            } finally {
                setCheckingLimits(false);
            }
        };

        checkPrerequisites();
    }, []);

    const handleFormSuccess = (propertyId: string, data: PropertyFormData) => {
        setCreatedPropertyId(propertyId);
        setMode('SINGLE_CERT');
    };

    const handleLimitReached = (tierName: string, current: number, limit: number) => {
        setUpgradeReason(`Your ${tierName} plan allows ${limit} ${limit === 1 ? 'property' : 'properties'}, and you've reached the limit (${current}/${limit}). Upgrade now to manage more properties and scale your portfolio.`);
        setShowUpgradeModal(true);
    };

    if (checkingLimits) {
        return <div className="min-h-screen flex items-center justify-center text-gray-500">
            <i className="fas fa-spinner fa-spin mr-2"></i> Loading compliance data...
        </div>;
    }

    return (
        <div className="bg-gray-50 min-h-screen font-sans flex flex-col">
            <Navbar />
            <UpgradeModal
                isOpen={showUpgradeModal}
                onClose={() => {
                    setShowUpgradeModal(false);
                    setUpgradeReason('');
                }}
                reason={upgradeReason}
            />
            <PhoneVerificationModal
                isOpen={showPhoneModal}
                onClose={() => navigate('/properties')}
                onVerified={() => setShowPhoneModal(false)}
            />

            <div className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10">
                <div className="mb-8">
                    <nav className="mb-4">
                        <button
                            onClick={() => mode === 'SELECTION' ? navigate('/properties') : setMode('SELECTION')}
                            className="text-sm text-gray-500 hover:text-gray-700 flex items-center"
                        >
                            <i className="fas fa-arrow-left mr-2"></i>
                            {mode === 'SELECTION' ? 'Back to Properties' : 'Back to Selection'}
                        </button>
                    </nav>

                    <h2 className="text-3xl font-bold text-gray-900">
                        {mode === 'SELECTION' && 'Add New Property'}
                        {mode === 'SINGLE_FORM' && 'Property Details'}
                        {mode === 'SINGLE_CERT' && 'Upload Certificates'}
                        {mode === 'BULK' && 'Bulk Upload'}
                    </h2>
                    <p className="mt-2 text-gray-600">
                        {mode === 'SELECTION' && 'Choose how you want to add properties to your portfolio.'}
                        {mode === 'SINGLE_FORM' && 'Enter the details of your property to start tracking compliance.'}
                        {mode === 'SINGLE_CERT' && 'Add compliance documents to your new property.'}
                        {mode === 'BULK' && 'Upload a CSV file to add multiple properties at once.'}
                    </p>
                </div>

                {mode === 'SELECTION' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div
                            onClick={() => setMode('SINGLE_FORM')}
                            className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 hover:border-emerald-500 hover:shadow-md cursor-pointer transition-all group"
                        >
                            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-emerald-200 transaction-colors">
                                <i className="fas fa-home text-xl text-emerald-600"></i>
                            </div>
                            <h3 className="text-lg font-bold text-gray-900">Single Property</h3>
                            <p className="text-sm text-gray-500 mt-2">
                                Manually enter details for a single property and upload its certificates. Best for small portfolios.
                            </p>
                        </div>

                        <div
                            onClick={() => setMode('BULK')}
                            className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 hover:border-blue-500 hover:shadow-md cursor-pointer transition-all group"
                        >
                            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-200 transaction-colors">
                                <i className="fas fa-file-csv text-xl text-blue-600"></i>
                            </div>
                            <h3 className="text-lg font-bold text-gray-900">Bulk Upload</h3>
                            <p className="text-sm text-gray-500 mt-2">
                                Upload a CSV file containing multiple properties. Best for migrating existing portfolios.
                            </p>
                        </div>
                    </div>
                )}

                {mode === 'SINGLE_FORM' && (
                    <PropertyForm
                        onSuccess={handleFormSuccess}
                        onCancel={() => setMode('SELECTION')}
                        onLimitReached={handleLimitReached}
                    />
                )}

                {mode === 'SINGLE_CERT' && createdPropertyId && (
                    <CertificateUploadStep
                        propertyId={createdPropertyId}
                        onFinish={() => navigate('/properties')}
                    />
                )}

                {mode === 'BULK' && (
                    <BulkUploadStep
                        onBack={() => setMode('SELECTION')}
                    />
                )}

            </div>

            <Footer />
        </div>
    );
}
